import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { AdminService, Equipment, BlockedPeriod } from '@app/core/services/admin.service';
import { ToastService } from '@app/core/services/toast.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-equipment-edit',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './equipment-edit.component.html',
  styleUrls: ['./equipment-edit.component.scss']
})
export class EquipmentEditComponent implements OnInit, OnDestroy {
  editingEquipment: Equipment | null = null;
  originalEquipmentId: string | null = null;
  isSaving = false;
  isLoading = true;
  newBlockedPeriod: BlockedPeriod = { startDate: '', endDate: '', comment: '' };
  private subscription = new Subscription();

  constructor(
    private adminService: AdminService,
    private toastService: ToastService,
    private router: Router,
    private route: ActivatedRoute,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.subscription.add(
      this.route.params.subscribe(params => {
        const equipmentId = params['id'];
        console.log('[EquipmentEdit] Route params changed:', { equipmentId });
        if (equipmentId) {
          // Decode URL-encoded ID (e.g., equipment%2F34-A -> equipment/34-A)
          const decodedId = decodeURIComponent(equipmentId);
          this.loadEquipment(decodedId);
        } else {
          // New equipment
          console.log('[EquipmentEdit] Creating new equipment');
          this.initializeNewEquipment();
          this.isLoading = false;
          this.cdr.detectChanges();
        }
      })
    );
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  loadEquipment(id: string) {
    console.log('[EquipmentEdit] Loading equipment with ID:', id);
    this.isLoading = true;
    this.adminService.getEquipment(id).subscribe({
      next: (equipment) => {
        console.log('[EquipmentEdit] Equipment loaded:', equipment);
        this.originalEquipmentId = equipment.id || null;
        this.editingEquipment = { ...equipment };
        // Stelle sicher, dass blockedPeriods ein Array ist
        if (!this.editingEquipment.blockedPeriods) {
          this.editingEquipment.blockedPeriods = [];
        }
        this.isLoading = false;
        this.cdr.detectChanges();
        console.log('[EquipmentEdit] isLoading set to false, change detection triggered');
      },
      error: (err) => {
        console.error('[EquipmentEdit] Error loading equipment:', err);
        this.isLoading = false;
        this.cdr.detectChanges();
        this.toastService.error('Equipment nicht gefunden');
        this.navigateBack();
      }
    });
  }

  initializeNewEquipment() {
    this.editingEquipment = {
      name: '',
      width: 2.5,
      height: 1.5,
      pixelPitch: 2.6,
      length: 2.66,
      deliveryHeight: 2.10,
      deliveryWidth: 0.90,
      isAvailable: true,
      blockedPeriods: []
    };
  }

  addBlockedPeriod() {
    if (!this.editingEquipment) return;
    if (!this.newBlockedPeriod.startDate || !this.newBlockedPeriod.endDate) {
      this.toastService.error('Start- und Enddatum erforderlich');
      return;
    }

    const startDate = new Date(this.newBlockedPeriod.startDate);
    const endDate = new Date(this.newBlockedPeriod.endDate);

    if (startDate >= endDate) {
      this.toastService.error('Enddatum muss nach Startdatum liegen');
      return;
    }

    if (!this.editingEquipment.blockedPeriods) {
      this.editingEquipment.blockedPeriods = [];
    }

    this.editingEquipment.blockedPeriods.push({
      startDate: this.newBlockedPeriod.startDate,
      endDate: this.newBlockedPeriod.endDate,
      comment: this.newBlockedPeriod.comment || ''
    });

    // Reset form
    this.newBlockedPeriod = { startDate: '', endDate: '', comment: '' };
    this.cdr.detectChanges();
    this.toastService.success('Sperrzeit hinzugefügt');
  }

  removeBlockedPeriod(index: number) {
    if (!this.editingEquipment?.blockedPeriods) return;
    this.editingEquipment.blockedPeriods.splice(index, 1);
    this.cdr.detectChanges();
    this.toastService.success('Sperrzeit entfernt');
  }

  formatDate(dateString: string): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', { 
      year: 'numeric', 
      month: '2-digit', 
      day: '2-digit' 
    });
  }

  saveEquipment() {
    if (!this.editingEquipment) return;

    this.isSaving = true;
    const saveObs = this.editingEquipment.id
      ? this.adminService.updateEquipment(this.editingEquipment.id, this.editingEquipment)
      : this.adminService.createEquipment(this.editingEquipment);

    saveObs.subscribe({
      next: () => {
        this.isSaving = false;
        this.toastService.success('Equipment gespeichert');
        this.navigateBack();
      },
      error: () => {
        this.isSaving = false;
        this.toastService.error('Fehler beim Speichern');
      }
    });
  }

  deleteEquipment() {
    if (!this.originalEquipmentId) return;
    
    if (!confirm('Equipment wirklich löschen?')) return;

    this.isSaving = true;
    this.adminService.deleteEquipment(this.originalEquipmentId).subscribe({
      next: () => {
        this.isSaving = false;
        this.toastService.success('Equipment gelöscht');
        this.navigateBack();
      },
      error: () => {
        this.isSaving = false;
        this.toastService.error('Fehler beim Löschen');
      }
    });
  }

  navigateBack() {
    this.router.navigate(['/admin']);
  }
}
